import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle } from "lucide-react";

interface MultipleChoiceQuestionProps {
  options: string[];
  currentAnswer: string[];
  onAnswer: (answer: string[]) => void;
  allowOther?: boolean;
}

const MultipleChoiceQuestion = ({ options, currentAnswer, onAnswer, allowOther }: MultipleChoiceQuestionProps) => {
  const [otherText, setOtherText] = useState("");

  const handleOtherSelect = () => {
    if (otherText.trim()) {
      const currentAnswers = Array.isArray(currentAnswer) ? currentAnswer : [];
      const hasOther = currentAnswers.some(a => a.startsWith("Other: "));
      
      if (hasOther) {
        // Remove existing "Other" and add new one
        const filteredAnswers = currentAnswers.filter(a => !a.startsWith("Other: "));
        onAnswer([...filteredAnswers, `Other: ${otherText}`]);
      } else {
        onAnswer([...currentAnswers, `Other: ${otherText}`]);
      }
      setOtherText("");
    }
  };

  return (
    <div className="space-y-3">
      {options.map((option, index) => {
        if (option === "Other" && allowOther) {
          const currentAnswers = Array.isArray(currentAnswer) ? currentAnswer : [];
          const isOtherSelected = currentAnswers.some(a => a.startsWith("Other: "));
          
          return (
            <div key={index} className="space-y-2">
              <Button
                variant={isOtherSelected ? "default" : "outline"}
                className={`w-full text-left justify-start p-4 h-auto min-h-[48px] whitespace-normal transition-all ${
                  isOtherSelected 
                    ? "bg-blue-600 hover:bg-blue-700 text-white" 
                    : "hover:bg-blue-50 hover:border-blue-200"
                }`}
                onClick={() => {
                  if (otherText.trim()) {
                    handleOtherSelect();
                  }
                }}
              >
                {isOtherSelected && <CheckCircle className="mr-2 h-4 w-4" />}
                Other (please specify)
              </Button>
              <Input
                placeholder="Please specify..."
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                className="ml-4"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && otherText.trim()) {
                    handleOtherSelect();
                  }
                }}
              />
            </div>
          );
        }

        const isSelected = Array.isArray(currentAnswer) && currentAnswer.includes(option);
        return (
          <Button
            key={index}
            variant={isSelected ? "default" : "outline"}
            className={`w-full text-left justify-start p-4 h-auto min-h-[48px] whitespace-normal transition-all ${
              isSelected 
                ? "bg-blue-600 hover:bg-blue-700 text-white" 
                : "hover:bg-blue-50 hover:border-blue-200"
            }`}
            onClick={() => {
              const currentAnswers = Array.isArray(currentAnswer) ? currentAnswer : [];
              if (isSelected) {
                onAnswer(currentAnswers.filter(a => a !== option));
              } else {
                onAnswer([...currentAnswers, option]);
              }
            }}
          >
            {isSelected && <CheckCircle className="mr-2 h-4 w-4" />}
            {option}
          </Button>
        );
      })}
    </div>
  );
};

export default MultipleChoiceQuestion;